import React, {
	Dispatch,
	RefAttributes,
	memo,
	useCallback,
	useMemo,
	useRef,
	useState,
} from "react";
import { Button, Stepper, react, TextBox, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useObjectEditor from "../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentSystemFrontEndType,
} from "../../../../../../services/PaymentSystem";
import {
	StyledColumn,
	StyledRow,
	StyledP,
} from "../../../../../../components/common";
import translationPath from "../../constants/translationPath";
import ValueStepper from "../ValueStepper";

import InternalController from "./Controller";
import DinarPaymentsCurrency from "./components/DinarPaymentsCurrency";
import DinarPaymentsCountryIso from "./components/DinarPaymentsCountryIso";

const DinarPaymentBase = react.withController<
	DinarPayment.PropsBase,
	DinarPayment.Controller
>(({ value, onChange, controller }) => {
	const { t } = useTranslation();

	const [countryIsoRef, setCountryIsoRef] =
		useRefWithSetter<DinarPaymentsCurrency.Ref | null>(null);
	const [currencyRef, setCurrencyRef] =
		useRefWithSetter<DinarPaymentsCountryIso.Ref | null>(null);

	const valueEditor = useObjectEditor(value, onChange);

	const type = valueEditor.useGetter("type");

	const isExecutor = useMemo(
		() =>
			PaymentSystemFrontEndType.EXECUTOR === type ||
			PaymentSystemFrontEndType.TEMPLATE_EXECUTOR === type,
		[type],
	);

	// systemFeeAdditionalForExecutor
	const systemFeeAdditionalForExecutorValue = valueEditor.useGetter(
		"systemFeeAdditionalForExecutor",
	);
	const setSystemFeeAdditionalForExecutorValue = valueEditor.useSetter(
		"systemFeeAdditionalForExecutor",
	);
	const handleSystemFeeAdditionalForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeeAdditionalForExecutorValue(value);
			}
		},
		[setSystemFeeAdditionalForExecutorValue],
	);

	const textSystemFeeAdditionalForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeeAdditionalForExecutor`,
				"Additional commission from the driver in UAH.",
			]),
		[t],
	);

	// systemFeePercentageForExecutor
	const systemFeePercentageForExecutorValue = valueEditor.useGetter(
		"systemFeePercentageForExecutor",
	);
	const setSystemFeePercentageForExecutorValue = valueEditor.useSetter(
		"systemFeePercentageForExecutor",
	);
	const handleSystemFeePercentageForExecutor = useCallback(
		(value: Stepper.Value) => {
			if (typeof value === "number") {
				setSystemFeePercentageForExecutorValue(value);
			}
		},
		[setSystemFeePercentageForExecutorValue],
	);

	const textSystemFeePercentageForExecutor = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.systemFeePercentageForExecutor`,
				"Additional driver commission in %",
			]),
		[t],
	);

	// dinarPayUsername
	const dinarPayUsername = valueEditor.useGetter("dinarPayUsername");
	const setDinarPayUsername = valueEditor.useSetter("dinarPayUsername");
	const [errorDinarPayUsername, setDinarPayUsernameError] =
		useState<boolean>(false);
	const handleDinarPayUsername = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setDinarPayUsername(value);
				setDinarPayUsernameError(false);
			}
		},
		[setDinarPayUsername, setDinarPayUsernameError],
	);

	const textErrorDinarPayUsername = useMemo(() => {
		if (!errorDinarPayUsername) return "";
		return t([
			`${translationPath}.modal.errors.paymentsKey`,
			"Username not entered",
		]);
	}, [t, errorDinarPayUsername]);

	const placeholderDinarPayUsername = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsKey`,
				"Enter username",
			]),
		[t],
	);

	const textDinarPayUsername = useMemo(
		() =>
			t([
				//  `${translationPath}.modal.text.paymentsKey`,
				"Username",
			]),
		[t],
	);

	// dinarPayPassword
	const dinarPayPassword = valueEditor.useGetter("dinarPayPassword");
	const dinarPayPasswordSet = valueEditor.useSetter("dinarPayPassword");
	const [errorDinarPayPassword, setDinarPayPasswordError] =
		useState<boolean>(false);
	const dinarPayPasswordHandle = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				dinarPayPasswordSet(value);
				setDinarPayPasswordError(false);
			}
		},
		[dinarPayPasswordSet, setDinarPayPasswordError],
	);

	const textErrorDinarPayPassword = useMemo(() => {
		if (!errorDinarPayPassword) return "";
		return t([
			`${translationPath}.modal.errors.paymentsSecretKey`,
			"Password not entered",
		]);
	}, [t, errorDinarPayPassword]);

	const placeholderDinarPayPassword = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.paymentsSecretKey2`,
				"Enter password",
			]),
		[t],
	);
	const textDinarPayPassword = useMemo(
		() =>
			t([
				// `${translationPath}.modal.text.paymentsSecretKey`,
				"Password",
			]),
		[t],
	);

	// dinarPayApiUrl
	const dinarPayApiUrl = valueEditor.useGetter("dinarPayApiUrl");
	const setDinarPayApiUrl = valueEditor.useSetter("dinarPayApiUrl");
	const [errorDinarPayApiUrl, setDinarPayApiUrlError] =
		useState<boolean>(false);
	const handleDinarPayApiUrl = useCallback(
		(value: string) => {
			if (typeof value === "string") {
				setDinarPayApiUrl(value);
				setDinarPayApiUrlError(false);
			}
		},
		[setDinarPayApiUrl],
	);

	const textErrorDinarPayApiUrl = useMemo(() => {
		if (!errorDinarPayApiUrl) return "";
		return t([
			`${translationPath}.modal.errors.maibSignatureId`,
			"API URL not entered",
		]);
	}, [t, errorDinarPayApiUrl]);

	const placeholderDinarPayApiUrl = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.maibSignatureId`,
				"Enter API URL",
			]),
		[t],
	);

	const textDinarPayApiUrl = useMemo(
		() =>
			t([
				//  `${translationPath}.modal.text.paymentsKey`,
				"API URL",
			]),
		[t],
	);

	// dinarPayCertificate (.p12 as base64)
	const dinarPayCertificate = valueEditor.useGetter("dinarPayCertificate");
	const setDinarPayCertificate = valueEditor.useSetter("dinarPayCertificate");
	const [errorDinarPayCertificate, setDinarPayCertificateError] =
		useState<string>("");

	const textDinarPayCertificate = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.dinarPayCertificate`,
				"Certificate .p12",
			]),
		[t],
	);

	const textDinarPayCertificateUpload = useMemo(
		() =>
			t([
				`${translationPath}.modal.buttons.dinarPayCertificateUpload`,
				"Upload .p12",
			]),
		[t],
	);

	const textDinarPayCertificateReplace = useMemo(
		() =>
			t([
				`${translationPath}.modal.buttons.dinarPayCertificateReplace`,
				"Certificate loaded — replace",
			]),
		[t],
	);

	const textDinarPayCertificateClear = useMemo(
		() =>
			t([
				`${translationPath}.modal.buttons.dinarPayCertificateClear`,
				"Remove",
			]),
		[t],
	);

	const textErrorDinarPayCertificate = useMemo(
		() =>
			t([
				`${translationPath}.modal.errors.dinarPayCertificate`,
				"Unable to read the certificate file",
			]),
		[t],
	);

	/**
	 * The certificate is kept in the configuration as the base64 of the .p12
	 * file: the server presents it on every request to api.cibpay.co (mTLS),
	 * without it the gateway answers 400 before any credential check.
	 */
	const handleDinarPayCertificateFile = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];

			// otherwise picking the same file twice does nothing
			event.target.value = "";

			if (!file) return;

			try {
				const dataUrl = await new Promise<string>((resolve, reject) => {
					const reader = new FileReader();

					reader.onloadend = () => resolve(reader.result as string);
					reader.onerror = () =>
						reject(new Error("Unable to read the file."));

					reader.readAsDataURL(file);
				});

				setDinarPayCertificate(
					dataUrl.slice(dataUrl.indexOf(",") + 1),
				);
				setDinarPayCertificateError("");
			} catch {
				setDinarPayCertificateError(textErrorDinarPayCertificate);
			}
		},
		[setDinarPayCertificate, textErrorDinarPayCertificate],
	);

	const certificateInputRef = useRef<HTMLInputElement | null>(null);

	const handleDinarPayCertificatePick = useCallback(() => {
		certificateInputRef.current?.click();
	}, []);

	const handleDinarPayCertificateClear = useCallback(() => {
		setDinarPayCertificate("");
		setDinarPayCertificateError("");
	}, [setDinarPayCertificate]);

	// dinarPayCertPassword
	const dinarPayCertPassword = valueEditor.useGetter("dinarPayCertPassword");
	const setDinarPayCertPassword = valueEditor.useSetter(
		"dinarPayCertPassword",
	);
	const handleDinarPayCertPassword = useCallback(
		(value: string) => {
			if (typeof value === "string") setDinarPayCertPassword(value);
		},
		[setDinarPayCertPassword],
	);

	const textDinarPayCertPassword = useMemo(
		() =>
			t([
				`${translationPath}.modal.text.dinarPayCertPassword`,
				"Certificate password",
			]),
		[t],
	);

	const placeholderDinarPayCertPassword = useMemo(
		() =>
			t([
				`${translationPath}.modal.placeholders.dinarPayCertPassword`,
				"Enter the certificate password",
			]),
		[t],
	);

	// Currency and CountryIso
	const textCurrency = useMemo(
		() => t([`${translationPath}.modal.text.currency`, "Currency"]),
		[t],
	);
	const textCountryIso = useMemo(
		() => t([`${translationPath}.modal.text.countryIso`, "Country"]),
		[t],
	);
	// controller
	controller.setContext({
		dinarPayPassword,
		dinarPayUsername,
		dinarPayApiUrl,
		currencyRef,
		countryIsoRef,
		setDinarPayPasswordError,
		setDinarPayUsernameError,
		setDinarPayApiUrlError,
	});

	return (
		<StyledColumn gap="10px" w="100%">
			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textDinarPayUsername} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderDinarPayUsername}
					error={textErrorDinarPayUsername}
					value={dinarPayUsername}
					onChange={handleDinarPayUsername}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textDinarPayPassword} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					error={textErrorDinarPayPassword}
					placeholder={placeholderDinarPayPassword}
					value={dinarPayPassword}
					onChange={dinarPayPasswordHandle}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textDinarPayApiUrl} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					error={textErrorDinarPayApiUrl}
					placeholder={placeholderDinarPayApiUrl}
					value={dinarPayApiUrl}
					onChange={handleDinarPayApiUrl}
				/>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textDinarPayCertificate} :</StyledP>
				<StyledRow alignItems="center" gap="10px">
					{/*
					 * The native input label cannot be used: its value is reset
					 * after a pick (so the same file can be picked again) and
					 * the browser then says "no file chosen" again. Hence the
					 * input is hidden and the button label carries the state.
					 */}
					<input
						ref={certificateInputRef}
						type="file"
						accept=".p12,.pfx"
						style={{ display: "none" }}
						onChange={handleDinarPayCertificateFile}
					/>
					<Button.Button
						style={{ padding: "5px 15px", height: "28px" }}
						text={
							dinarPayCertificate
								? textDinarPayCertificateReplace
								: textDinarPayCertificateUpload
						}
						onClick={handleDinarPayCertificatePick}
					/>
					{!!dinarPayCertificate && (
						<Button.Button
							style={{ padding: "5px 15px", height: "28px" }}
							text={textDinarPayCertificateClear}
							onClick={handleDinarPayCertificateClear}
						/>
					)}
					{!!errorDinarPayCertificate && (
						<StyledP>{errorDinarPayCertificate}</StyledP>
					)}
				</StyledRow>
			</StyledRow>

			<StyledRow alignItems="center" gap="10px" w="100%">
				<StyledP w="200px">{textDinarPayCertPassword} :</StyledP>
				<TextBox.TextBox
					style={{ width: "calc(100% - 200px)" }}
					placeholder={placeholderDinarPayCertPassword}
					value={dinarPayCertPassword}
					onChange={handleDinarPayCertPassword}
				/>
			</StyledRow>

			{isExecutor && (
				<>
					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeePercentageForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeePercentageForExecutorValue}
							onChange={handleSystemFeePercentageForExecutor}
						/>
					</StyledRow>

					<StyledRow alignItems="center" gap="10px" w="100%">
						<StyledP w="200px">
							{textSystemFeeAdditionalForExecutor}:
						</StyledP>
						<ValueStepper
							width="80px"
							min={0}
							value={systemFeeAdditionalForExecutorValue}
							onChange={handleSystemFeeAdditionalForExecutor}
						/>
					</StyledRow>
				</>
			)}

			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCurrency}:</StyledP>
				<DinarPaymentsCurrency
					ref={setCurrencyRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
			<StyledRow alignItems="center" gap="10px">
				<StyledP w="200px">{textCountryIso}:</StyledP>
				<DinarPaymentsCountryIso
					ref={setCountryIsoRef}
					value={value}
					onChange={onChange}
				/>
			</StyledRow>
		</StyledColumn>
	);
}, InternalController);

const DinarPayment = memo(DinarPaymentBase);

declare namespace DinarPayment {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default DinarPayment;
